import { useSyncExternalStore } from 'react';
import {
  GifFrame,
  GifSettings,
  GifCreatorState,
  DEFAULT_GIF_SETTINGS,
  GIF_LIMITS,
  prepareFramesForEncoding,
  fpsToMs,
  TextOverlay,
  CropRegion,
  GifTemplateId,
  GIF_TEMPLATES,
  WatermarkSettings,
} from './types';
import { track } from '@/lib/track';

// Store actions interface
interface GifStoreActions {
  addFrames: (frames: Omit<GifFrame, 'order'>[]) => void;
  removeFrame: (frameId: string) => void;
  clearFrames: () => void;
  reorderFrames: (fromIndex: number, toIndex: number) => void;
  moveFrameUp: (frameId: string) => void;
  moveFrameDown: (frameId: string) => void;
  reverseFrames: () => void;
  duplicateFrame: (frameId: string) => void;
  updateFrameDuration: (frameId: string, duration: number) => void;
  setFrames: (frames: GifFrame[]) => void;
  updateSettings: (settings: Partial<GifSettings>) => void;
  applyTemplate: (templateId: GifTemplateId) => void;
  setSpeedMultiplier: (multiplier: number) => void;
  setWatermark: (watermark: WatermarkSettings | undefined) => void;
  setCrop: (crop: CropRegion | undefined) => void;
  addTextOverlay: (overlay: Omit<TextOverlay, 'id'>) => void;
  updateTextOverlay: (overlayId: string, updates: Partial<TextOverlay>) => void;
  removeTextOverlay: (overlayId: string) => void;
  resetSettings: () => void;
  setError: (error: string | null) => void;
  startEncoding: () => Promise<void>;
  cancelEncoding: () => void;
  downloadGif: (filename?: string) => void;
  clearOutput: () => void;
  saveGifToCloud: () => Promise<void>;
  reset: () => void;
}

type GifStore = GifCreatorState & GifStoreActions;

// Create a simple event-based store without external dependencies

type Listener = () => void;

const listeners = new Set<Listener>();

let state: GifCreatorState = {
  frames: [],
  settings: { ...DEFAULT_GIF_SETTINGS },
  isEncoding: false,
  encodingProgress: 0,
  previewUrl: null,
  outputBlob: null,
  error: null,
  isSaving: false,
  savedGifId: null,
};

// Cached snapshot - IMPORTANT: must be cached to avoid React error #185
let cachedSnapshot: GifStore | null = null;

// Worker instance
let encoderWorker: Worker | null = null;

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getState = (): GifCreatorState => state;

const setState = (partial: Partial<GifCreatorState>) => {
  state = { ...state, ...partial };
  // Invalidate cached snapshot when state changes
  cachedSnapshot = null;
  listeners.forEach((listener) => listener());
};

// Actions
const addFrames = (newFrames: Omit<GifFrame, 'order'>[]) => {
  const currentMax = state.frames.reduce((max, f) => Math.max(max, f.order), -1);
  const framesWithOrder: GifFrame[] = newFrames.map((frame, index) => ({
    ...frame,
    order: currentMax + 1 + index,
  }));
  
  const allFrames = [...state.frames, ...framesWithOrder];
  
  if (allFrames.length > GIF_LIMITS.WARNING_FRAME_COUNT) {
    console.warn(`GIF has ${allFrames.length} frames. Consider reducing for better performance.`);
  }
  
  if (allFrames.length > GIF_LIMITS.MAX_FRAME_COUNT) {
    setState({
      frames: allFrames.slice(0, GIF_LIMITS.MAX_FRAME_COUNT),
      error: `Maximum ${GIF_LIMITS.MAX_FRAME_COUNT} frames allowed. Some frames were not added.`,
    });
    return;
  }
  
  setState({ frames: allFrames, error: null });
};

const removeFrame = (frameId: string) => {
  const filtered = state.frames.filter((f) => f.id !== frameId);
  const reordered = filtered.map((f, index) => ({ ...f, order: index }));
  setState({ frames: reordered });
};

const clearFrames = () => {
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }
  setState({ frames: [], previewUrl: null, outputBlob: null, error: null });
};

const reorderFrames = (fromIndex: number, toIndex: number) => {
  const frames = [...state.frames].sort((a, b) => a.order - b.order);
  const [moved] = frames.splice(fromIndex, 1);
  frames.splice(toIndex, 0, moved);
  const reordered = frames.map((f, index) => ({ ...f, order: index }));
  setState({ frames: reordered });
};

const moveFrameUp = (frameId: string) => {
  const sorted = [...state.frames].sort((a, b) => a.order - b.order);
  const idx = sorted.findIndex((f) => f.id === frameId);
  if (idx <= 0) return;
  reorderFrames(idx, idx - 1);
};

const moveFrameDown = (frameId: string) => {
  const sorted = [...state.frames].sort((a, b) => a.order - b.order);
  const idx = sorted.findIndex((f) => f.id === frameId);
  if (idx < 0 || idx >= sorted.length - 1) return;
  reorderFrames(idx, idx + 1);
};

const reverseFrames = () => {
  const sorted = [...state.frames].sort((a, b) => a.order - b.order);
  const reversed = sorted.reverse().map((f, index) => ({ ...f, order: index }));
  setState({ frames: reversed });
};

const duplicateFrame = (frameId: string) => {
  const sorted = [...state.frames].sort((a, b) => a.order - b.order);
  const idx = sorted.findIndex((f) => f.id === frameId);
  if (idx < 0) return;
  if (sorted.length >= GIF_LIMITS.MAX_FRAME_COUNT) {
    setState({ error: `Maximum ${GIF_LIMITS.MAX_FRAME_COUNT} frames allowed.` });
    return;
  }
  const original = sorted[idx];
  const copy: GifFrame = {
    ...original,
    id: `${original.id}-copy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    order: idx + 1,
  };
  const next = [...sorted.slice(0, idx + 1), copy, ...sorted.slice(idx + 1)].map((f, i) => ({ ...f, order: i }));
  setState({ frames: next });
};

const updateFrameDuration = (frameId: string, duration: number) => {
  setState({
    frames: state.frames.map((f) =>
      f.id === frameId 
        ? { ...f, duration: Math.max(GIF_LIMITS.MIN_DURATION_MS, Math.min(GIF_LIMITS.MAX_DURATION_MS, duration)) }
        : f
    ),
  });
};

const setFrames = (frames: GifFrame[]) => {
  setState({ frames: frames.map((f, index) => ({ ...f, order: index })) });
};

const updateSettings = (newSettings: Partial<GifSettings>) => {
  const settings = { ...state.settings, ...newSettings };
  
  if (newSettings.frameRate !== undefined) {
    settings.defaultFrameDuration = fpsToMs(newSettings.frameRate);
  }
  
  setState({ settings });
};

const resetSettings = () => {
  setState({ settings: { ...DEFAULT_GIF_SETTINGS } });
};

const applyTemplate = (templateId: GifTemplateId) => {
  const template = GIF_TEMPLATES.find((t) => t.id === templateId);
  if (!template) return;
  const nextSettings: GifSettings = {
    ...state.settings,
    ...template.settings,
  };
  // Recompute defaultFrameDuration from frameRate if it changed
  if (template.settings.frameRate !== undefined) {
    nextSettings.defaultFrameDuration = fpsToMs(template.settings.frameRate);
  }
  setState({ settings: nextSettings });
};

const setSpeedMultiplier = (multiplier: number) => {
  const m = Math.max(0.25, Math.min(4, multiplier));
  setState({ settings: { ...state.settings, speedMultiplier: m } });
};

const setWatermark = (watermark: WatermarkSettings | undefined) => {
  setState({ settings: { ...state.settings, watermark } });
};

const setCrop = (crop: CropRegion | undefined) => {
  setState({ settings: { ...state.settings, crop } });
};

const addTextOverlay = (overlay: Omit<TextOverlay, 'id'>) => {
  const overlays = state.settings.textOverlays || [];
  if (overlays.length >= 3) {
    setState({ error: 'Maximum 3 text overlays allowed' });
    return;
  }
  const newOverlay: TextOverlay = {
    ...overlay,
    id: `text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };
  setState({
    settings: {
      ...state.settings,
      textOverlays: [...overlays, newOverlay],
    },
  });
};

const updateTextOverlay = (overlayId: string, updates: Partial<TextOverlay>) => {
  const overlays = state.settings.textOverlays || [];
  setState({
    settings: {
      ...state.settings,
      textOverlays: overlays.map((o) => (o.id === overlayId ? { ...o, ...updates } : o)),
    },
  });
};

const removeTextOverlay = (overlayId: string) => {
  const overlays = state.settings.textOverlays || [];
  setState({
    settings: {
      ...state.settings,
      textOverlays: overlays.filter((o) => o.id !== overlayId),
    },
  });
};

const setError = (error: string | null) => {
  setState({ error });
};

const setPreviewUrl = (url: string | null) => {
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }
  setState({ previewUrl: url });
};

const startEncoding = async () => {
  const { frames, settings } = state;
  
  if (frames.length === 0) {
    setState({ error: 'No frames to encode' });
    return;
  }
  
  setState({ isEncoding: true, encodingProgress: 0, error: null });
  
  try {
    if (encoderWorker) {
      encoderWorker.terminate();
    }
    
    encoderWorker = new Worker(
      new URL('./gif-encoder.worker.ts', import.meta.url),
      { type: 'module' }
    );
    
    const preparedFrames = prepareFramesForEncoding(frames, settings);
    
    encoderWorker.onmessage = (event) => {
      const response = event.data;
      
      switch (response.type) {
        case 'progress':
          setState({ encodingProgress: response.percent });
          break;
          
        case 'complete':
          setPreviewUrl(response.gifDataUrl);
          setState({
            outputBlob: response.gifBlob,
            isEncoding: false,
            encodingProgress: 100,
          });
          track('generated', 'gif', response.gifBlob ? response.gifBlob.size : undefined);
          break;
          
        case 'error':
          setState({ error: response.message, isEncoding: false });
          break;
      }
    };
    
    encoderWorker.onerror = (error) => {
      setState({ error: `Encoding failed: ${error.message}`, isEncoding: false });
    };
    
    encoderWorker.postMessage({
      type: 'encode',
      frames: preparedFrames,
      settings,
    });
  } catch (error) {
    setState({
      error: error instanceof Error ? error.message : 'Encoding failed',
      isEncoding: false,
    });
  }
};

const cancelEncoding = () => {
  if (encoderWorker) {
    encoderWorker.terminate();
    encoderWorker = null;
  }
  setState({ isEncoding: false, encodingProgress: 0 });
};

const downloadGif = (filename = 'animation.gif') => {
  if (!state.outputBlob) return;
  
  const url = URL.createObjectURL(state.outputBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  track('exported', 'gif', state.outputBlob.size);
};

// Clear the generated GIF output without clearing frames
const clearOutput = () => {
  cancelEncoding();
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }
  setState({
    previewUrl: null,
    outputBlob: null,
    error: null,
    savedGifId: null,
  });
};

const reset = () => {
  cancelEncoding();
  if (state.previewUrl) {
    URL.revokeObjectURL(state.previewUrl);
  }
  setState({
    frames: [],
    settings: { ...DEFAULT_GIF_SETTINGS },
    isEncoding: false,
    encodingProgress: 0,
    previewUrl: null,
    outputBlob: null,
    error: null,
    isSaving: false,
    savedGifId: null,
  });
};

const saveGifToCloud = async () => {
  const { outputBlob } = state;
  
  if (!outputBlob) {
    setState({ error: 'No GIF to save. Generate a GIF first.' });
    return;
  }
  
  setState({ isSaving: true, error: null });
  
  const savedGifId = crypto.randomUUID();
  localStorage.setItem(`framex.savedGif.${savedGifId}`, JSON.stringify({
    id: savedGifId,
    size: outputBlob.size,
    savedAt: new Date().toISOString(),
  }));

  setState({
    isSaving: false,
    savedGifId,
    error: null,
  });
};

// Actions object (singleton)
const actions: GifStoreActions = {
  addFrames,
  removeFrame,
  clearFrames,
  reorderFrames,
  moveFrameUp,
  moveFrameDown,
  reverseFrames,
  duplicateFrame,
  updateFrameDuration,
  setFrames,
  updateSettings,
  applyTemplate,
  setSpeedMultiplier,
  setWatermark,
  setCrop,
  addTextOverlay,
  updateTextOverlay,
  removeTextOverlay,
  resetSettings,
  setError,
  startEncoding,
  cancelEncoding,
  downloadGif,
  clearOutput,
  saveGifToCloud,
  reset,
};

// Get store snapshot with actions - CACHED to avoid React error #185
const getSnapshot = (): GifStore => {
  if (!cachedSnapshot) {
    cachedSnapshot = { ...getState(), ...actions };
  }
  return cachedSnapshot;
};

// Server snapshot (for SSR)
const serverSnapshot: GifStore = {
  frames: [],
  settings: { ...DEFAULT_GIF_SETTINGS },
  isEncoding: false,
  encodingProgress: 0,
  previewUrl: null,
  outputBlob: null,
  error: null,
  isSaving: false,
  savedGifId: null,
  ...actions,
};

const getServerSnapshot = (): GifStore => serverSnapshot;

// React hook for using the store
export function useGifStore<T>(selector: (state: GifStore) => T): T {
  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return selector(store);
}

// Additional exports for convenience
export const useGifFrames = () => useGifStore((s) => s.frames);
export const useGifSettings = () => useGifStore((s) => s.settings);
export const useGifEncoding = () => useGifStore((s) => ({
  isEncoding: s.isEncoding,
  progress: s.encodingProgress,
}));
export const useGifPreview = () => useGifStore((s) => s.previewUrl);
export const useGifError = () => useGifStore((s) => s.error);
export const useGifSaving = () => useGifStore((s) => ({
  isSaving: s.isSaving,
  savedGifId: s.savedGifId,
}));
